/**
 * The HR Director's queue — verified claims awaiting final approval, oldest first.
 *
 * Their whole job in one screen: who verified it, when, and both evidence links as real
 * hyperlinks, because the Director's signature is what settles the ledger.
 *
 * Visible to every admin, deliberately, so an admin can see the queue is waiting on someone.
 * Whether *they* can clear it is stated at the top rather than discovered by clicking — the
 * Director hat comes from an assignment, never from holding `admin` (D-39).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ClaimStatusBadge, Money, PageHeader, RefLink } from '@/components/bits';
import { Alert, Badge, Card, EmptyState, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { daysBetween, formatDate, sgtToday } from '@/lib/dates';

export function HrDirector() {
  const session = useSession();
  const claims = useQuery({
    queryKey: ['claims', 'director', session.year],
    queryFn: () => api.claims({ budget_year: session.year, status: ['hr_director'], limit: 100 }),
  });

  const items = [...(claims.data?.items ?? [])].sort((a, b) =>
    a.updated_at.localeCompare(b.updated_at),
  );
  const today = sgtToday();

  return (
    <>
      <PageHeader
        title="Director approvals"
        subtitle="Verified claims awaiting the signature that settles the ledger."
      />

      {session.me.capabilities.hr_director ? (
        <Alert tone="good" title="You are a designated HR Director" className="mb-4">
          You may give final approval on any claim here, except one you verified yourself — the
          verifier and the approver must differ (D-39).
        </Alert>
      ) : (
        <Alert tone="neutral" title="You are not a designated HR Director" className="mb-4">
          This queue is here so you can see what is waiting. Final approval needs an
          <strong> hr_director_assignments</strong> row, which holding <code>admin</code> does not
          give — that asymmetry is what keeps verification and approval two separate signatures.
        </Alert>
      )}

      <Card title={`${items.length} awaiting approval`}>
        {claims.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState title="Nothing awaiting final approval">
            Claims arrive here once an admin has verified the figures and archived the evidence.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Department</Th>
                <Th>Date</Th>
                <Th numeric>Cost</Th>
                <Th numeric>Reimbursable</Th>
                <Th numeric>Excess</Th>
                <Th>Waiting</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const days = daysBetween(item.updated_at.slice(0, 10), today);
                return (
                  <tr key={item.id}>
                    <Td>
                      <RefLink to={`/claims/${item.id}`} refNo={item.ref_no} />
                      {item.proposal_ref_no ? (
                        <p className="text-muted-foreground text-xs">{item.proposal_ref_no}</p>
                      ) : null}
                    </Td>
                    <Td>{activityLabel(item.activity_type)}</Td>
                    <Td className="text-xs">{item.department_name}</Td>
                    <Td className="whitespace-nowrap">{formatDate(item.actual_date)}</Td>
                    <Td numeric>
                      <Money cents={item.total_cost_cents} />
                    </Td>
                    <Td numeric>
                      <Money cents={item.reimbursable_cents} />
                    </Td>
                    <Td numeric>
                      <Money cents={item.excess_cents} muted />
                    </Td>
                    <Td>
                      <span
                        className={days >= 5 ? 'text-[var(--color-warning)] text-sm' : 'text-sm'}
                      >
                        {days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex flex-col items-start gap-1">
                        <ClaimStatusBadge status={item.status} />
                        {item.pending_exceptions > 0 ? (
                          <Badge tone="warn">{item.pending_exceptions} to decide</Badge>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
